import React, { useContext } from 'react';

import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID } from '@reverbdotcom/commons/src/constants';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useExpEnabled, useUser, useEppoClient } from '@reverbdotcom/commons/src/user_hooks';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { isEppoExpEnabled, EppoSubjectTypes } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';

import { SellFormContext } from '../../SellFormContext';
import { buildPackageEntry } from '../../../../discovery/shipping_labels/shipping_label_package';
import { carrierCalculatedPackageValidator } from './carrierCalculatedPackageValidator';
import { SelectedCarrier } from './SelectedCarrier';
import CarrierCalculatedForm from './CarrierCalculatedForm';
import { CarrierHighlights } from './CarrierHighlights';
import { Field, ActionType } from '../../sellFormReducerTypes';
import CarrierCalculatedEstimator from './CarrierCalculatedEstimator';
import CarrierCalculatedEstimatorV2 from './CarrierCalculatedEstimatorV2';

export default function CarrierCalculatedFormContainer() {
  const { currentCanonicalProductId, dispatch, listingEntry, listingState } = useContext(SellFormContext);
  const userInDisableExperiment = useExpEnabled(experiments.DISABLE_PACKAGE_SUGGESTIONS);
  const user = useUser();
  const eppoClient = useEppoClient();

  const userInEstimatorV2Experiment = isEppoExpEnabled({
    eppoClient,
    user,
    flagKey: eppoFlags.SELL_FORM_CARRIER_CALCULATED_ESTIMATOR_V2,
    subjectKey: user.id,
    subjectType: EppoSubjectTypes.USER_ID,
  });

  const localizedContents = listingEntry[Field.LOCALIZED_CONTENTS].find((lc) => lc.defaultForShop);
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const currentPackageEntryCameFromReverbSuggestion = !!packageEntry.packageSizeSuggestionId;
  const headerContent = !packageEntry.allowedCarriers?.length ? <CarrierHighlights /> : <SelectedCarrier carrier={packageEntry.allowedCarriers[0]} />;
  const hasGuitarRootCategory = [ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID].includes(listingEntry[Field.ROOT_CATEGORY_UUID]);

  function setPackageEntry(updatedPackage: core_apimessages_ShippingLabelPackage) {
    const upcomingPackageValidator = carrierCalculatedPackageValidator(updatedPackage);
    const hasValidSelectedCarrier = !upcomingPackageValidator?.errorTranslationKey && upcomingPackageValidator?.carrier;
    const allowedCarriers = hasValidSelectedCarrier ? [upcomingPackageValidator?.carrier] : null;

    dispatch({
      type: ActionType.UPDATE_FIELD,
      fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
      value: { ...updatedPackage, ...{ allowedCarriers } },
    });
  }

  function onPackageSuggestionResponse(respData) {
    const suggestionData = respData?.canonicalproduct?.packageSizeSuggestion;
    const validSuggestionData = !!suggestionData && !carrierCalculatedPackageValidator(suggestionData)?.errorTranslationKey;
    const currentPackageEntryIsBlank = ['length', 'width', 'height', 'weight'].every((dimensionKey) => !packageEntry[dimensionKey].value);
    const suggestionDiffersFromCurrentEntry = validSuggestionData && ['length', 'width', 'height', 'weight'].some((dimensionKey) => {
      return Number(suggestionData[dimensionKey]?.value) !== Number(packageEntry[dimensionKey]?.value) ||
        suggestionData[dimensionKey]?.unit !== packageEntry[dimensionKey]?.unit;
    });

    if (!validSuggestionData) { return; }

    const packageEntryFromSuggestionData = buildPackageEntry(
      {
        ...suggestionData,
        forceSafeShipping: true,
        forceSignatureRequired: true,
      },
      undefined,
      suggestionData.id,
    );

    const shouldActivateSuggestion = (currentPackageEntryIsBlank || currentPackageEntryCameFromReverbSuggestion) && suggestionDiffersFromCurrentEntry;

    if (shouldActivateSuggestion) {
      trackEvent({
        eventName: MParticleEventName.PackageSuggestionAvailable,
        rootCategoryUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
        packageSizeSuggestionId: suggestionData.id,
        experiments: formatExperimentsForEventAttributes(user, [experiments.DISABLE_PACKAGE_SUGGESTIONS]),
      });

      if (userInDisableExperiment) { return; }

      setPackageEntry(packageEntryFromSuggestionData);
    }
  }

  function trackGuitarPackageChange(dimension) {
    if (hasGuitarRootCategory) {
      trackEvent({
        eventName: MParticleEventName.UpdatedGuitarShippingLength,
        quantity: dimension,
        rootCategoryUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
        brand: localizedContents[Field.MAKE],
        cpId: currentCanonicalProductId,
        model: localizedContents[Field.MODEL],
      });
    }
  }

  const { loading } = useQuery(
    FETCH_PACKAGE_SIZE_SUGGESTION,
    {
      skip: !currentCanonicalProductId || listingState !== 'draft',
      variables: { canonicalProductId: currentCanonicalProductId },
      onCompleted: onPackageSuggestionResponse,
    },
  );

  if (loading) {
    return (
      <div className="d-flex fx-align-center fx-justify-center mtb-2 mobile-mt-4">
        <RCLoadingBars />
      </div>
    );
  }

  return (
    <>
      <div
        className="mt-2"
      >
        {/* We always show the SelectedCarrier UI here for guitars. */}
        {/* For other categories, the same UI is rendered in CarrierCalculatedEstimator. */}
        {hasGuitarRootCategory && !userInEstimatorV2Experiment && (
          <div className="carrier-calculated-form-container__header">
            {headerContent}
          </div>
        )}
        <div className="d-flex fx-dir-col">
          <CarrierCalculatedForm
            packageEntry={packageEntry}
            setPackageEntry={setPackageEntry}
            trackEvent={trackGuitarPackageChange}
            userInEstimatorV2Experiment={userInEstimatorV2Experiment}
          />
        </div>
      </div>
      {userInEstimatorV2Experiment && (<CarrierCalculatedEstimatorV2 />)}
      {!userInEstimatorV2Experiment && (<CarrierCalculatedEstimator />)}
    </>
  );
}

export const FETCH_PACKAGE_SIZE_SUGGESTION = gql(`
  query Core_SellForm_FetchPackageSizeSuggestion($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      packageSizeSuggestion {
        _id
        id
        length {
          unit
          value
        }
        width {
          unit
          value
        }
        height {
          unit
          value
        }
        weight {
          unit
          value
        }
      }
    }
  },
`);
